import type { QueryKey } from '@tanstack/react-query';
import {
  apiListQuery,
  apiQuery,
  createQueryKeys,
  useApiMutation,
  type ApiMutationOptions,
} from '@/shared/data';
import type { StepUpGrant } from '@/shared/http';
import { getCurrentTenant } from '@/shared/http/client';
import {
  deleteUserGroup,
  getUserGroup,
  insertGroupMember,
  listUserGroupOptions,
  removeGroupMember,
  saveUserGroup,
} from './user-group.api';
import type { UserGroupBody } from './user-group.wire';
const keys = createQueryKeys('user-group');
export const userGroupKeys = keys;
export function userGroupOptionsQuery(params: { tenant?: string | undefined } = {}) {
  const resolved = { tenant: params.tenant ?? getCurrentTenant() };
  return apiListQuery(keys.custom('options', 'user-group', resolved), ({ signal }) =>
    listUserGroupOptions(resolved, signal),
  );
}

/** GET do grupo (`groupContext.tsx:36-79`); `novo` não busca. */
export function userGroupDetailQuery(groupName: string) {
  const tenant = getCurrentTenant();
  return apiQuery(
    keys.custom('detail', groupName, tenant ?? ''),
    ({ signal }) => getUserGroup(groupName, tenant, signal),
    { enabled: groupName !== 'novo' && groupName !== '' },
  );
}

const def = <V, D>(run: (variables: V) => Promise<D>, invalidate: readonly QueryKey[] = [keys.all]) => ({
  run,
  invalidate,
});
/** Toda escrita de Segurança leva a autorização do step-up novo (`X-UY3-StepUp`), pedida pela tela. */
type Authorized = { readonly stepUp: StepUpGrant };
export const userGroupMutations = {
  save: def(({ groupId, body, stepUp }: { groupId: string | undefined; body: UserGroupBody } & Authorized) =>
    saveUserGroup(groupId, body, getCurrentTenant(), stepUp),
  ),
  delete: def(({ groupName, stepUp }: { groupName: string } & Authorized) => deleteUserGroup(groupName, getCurrentTenant(), stepUp)),
  insertMember: def(({ groupName, userId, stepUp }: { groupName: string; userId: string } & Authorized) =>
    insertGroupMember(groupName, userId, getCurrentTenant(), stepUp),
  ),
  removeMember: def(({ groupName, userId, stepUp }: { groupName: string; userId: string } & Authorized) =>
    removeGroupMember(groupName, userId, getCurrentTenant(), stepUp),
  ),
};
type Name = keyof typeof userGroupMutations;
type Variables<K extends Name> = Parameters<(typeof userGroupMutations)[K]['run']>[0];
type Data<K extends Name> = Awaited<ReturnType<(typeof userGroupMutations)[K]['run']>>;

/** Mutação genérica do módulo (X07); o feedback é da tela (X06). */
export function useUserGroupMutation<K extends Name>(
  name: K,
  options: Omit<ApiMutationOptions<Data<K>, Variables<K>>, 'mutationFn'> = {},
) {
  const entry = userGroupMutations[name] as unknown as {
    run: (variables: Variables<K>) => Promise<Data<K>>;
    invalidate: readonly QueryKey[];
  };
  return useApiMutation<Data<K>, Variables<K>>({
    mutationKey: [...keys.all, 'mutation', name],
    success: false,
    error: false,
    invalidate: entry.invalidate,
    ...options,
    mutationFn: entry.run,
  });
}
